import { useState } from 'react';
import type { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Wraps the application in TanStack Query's context with sensible defaults
 * for a B2B portal:
 *  - `staleTime: 60s` — modules can opt-in to longer caching per query key.
 *  - `retry: 1` — one silent retry on transient failures before surfacing.
 *  - `refetchOnWindowFocus: false` — pages re-fetch via explicit invalidation
 *    in mutation handlers, not on every tab swap.
 *
 * The `QueryClient` is owned by component state so HMR-reloaded children do
 * not orphan in-flight queries.
 */
export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
          mutations: {
            retry: 0,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  );
}
